import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
    ArrowDownTrayIcon,
    ArrowTopRightOnSquareIcon,
    DocumentTextIcon,
    EyeIcon,
    MagnifyingGlassIcon,
    MusicalNoteIcon,
} from '@heroicons/react/24/outline'
import SectionHeader from '../../../../Components/GeneralComponents/SectionHeader'
import FilePreviewModal from '../../../../Components/Files/FilePreviewModal'
import ParticleLoader from "../../../../Components/ParticleLoader";
import { getTeamFiles } from '../../../../Requests/TeamRequests'
import { getFilePreview } from '../../../../Requests/NewAnalysisRequests'
import { getTeamUuid } from '../../../../utils/cookies'
import { formatDateTime } from '../../../../utils/calcs'

const statusStyles = {
    "Processed": "bg-gray-900 text-white",
    "Processing": "bg-amber-100 text-amber-800",
}

function Files() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [files, setFiles] = useState(null)
    const [search, setSearch] = useState("")
    const [previewFile, setPreviewFile] = useState(null)
    const [downloading, setDownloading] = useState(null)

    // The stored URL is private, so a download needs a freshly signed one.
    const handleDownload = (file) => {
        setDownloading(file.uuid)
        getFilePreview(file.uuid).then((res) => {
            if (res.data.url) {
                window.open(res.data.url, '_blank', 'noreferrer')
            }
        }).catch(() => {
        }).finally(() => {
            setDownloading(null)
        })
    }

    useEffect(() => {
        getTeamFiles(getTeamUuid()).then((res) => {
            setFiles(res.data)
        }).catch(() => {
            setFiles([])
        })
    }, [])

    const term = search.trim().toLowerCase()
    const visibleFiles = (files || []).filter((file) =>
        !term ||
        (file.name || "").toLowerCase().includes(term) ||
        (file.project?.name || "").toLowerCase().includes(term)
    )

    // Grouped under a project heading, projects ordered by their newest file.
    const groups = []
    visibleFiles.forEach((file) => {
        const uuid = file.project?.uuid || "none"
        const group = groups.find((candidate) => candidate.uuid === uuid)
        if (group) {
            group.files.push(file)
        } else {
            groups.push({ uuid, name: file.project?.name, files: [file] })
        }
    })

    return (
        <div className='md:pl-64 pt-4 md:pt-12 w-full flex justify-center px-4 pb-12'>
            <div className='flex w-full max-w-7xl flex-col gap-4'>
                <div className='bg-white rounded-2xl w-full shadow-xl'>
                    <SectionHeader title={t('Files.Title')} subtitle={t('Files.Subtitle')} />
                </div>

                <div className='rounded-2xl bg-white p-4 shadow-xl'>
                    <div className='relative mb-4'>
                        <MagnifyingGlassIcon className='pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-gray-400' />
                        <input
                            type='text'
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t('Files.SearchPlaceholder')}
                            className='w-full rounded-lg border-gray-300 pl-10 text-sm focus:border-black focus:ring-black'
                        />
                    </div>

                    {!files && <div className="flex justify-center"><ParticleLoader size={48} /></div>}

                    {files && visibleFiles.length === 0 &&
                        <p className='py-8 text-center text-sm text-gray-500'>{t('Files.NoFiles')}</p>
                    }

                    {groups.map((group) => (
                        <div key={group.uuid} className='mb-2'>
                            <div className='flex items-center gap-3 pt-4'>
                                <span className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                                    {group.name || t('Files.NoProject')}
                                </span>
                                <span className='h-px flex-1 bg-gray-200' />
                                <span className='text-xs text-gray-400'>{group.files.length}</span>
                            </div>
                            <div className='flex flex-col divide-y divide-gray-100'>
                                {group.files.map((file) => (
                                    <div key={file.uuid} className='flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between'>
                                        <div className='flex min-w-0 items-center gap-3'>
                                            <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-black text-white'>
                                                {file.file_type === 'audio'
                                                    ? <MusicalNoteIcon className='h-5 w-5' />
                                                    : <DocumentTextIcon className='h-5 w-5' />}
                                            </div>
                                            <div className='min-w-0'>
                                                <div className='truncate text-sm font-medium text-gray-900'>{file.name}</div>
                                                <div className='truncate text-xs text-gray-500'>
                                                    {t(`Files.Type.${file.file_type}`, file.file_type)} · {formatDateTime(file.created_at)}
                                                </div>
                                            </div>
                                        </div>

                                        <div className='flex shrink-0 flex-wrap items-center gap-2'>
                                            <span className={'rounded-full px-3 py-1 text-xs font-medium ' + (statusStyles[file.status] || 'bg-gray-100 text-gray-600')}>
                                                {t(`Files.Status.${file.status}`, file.status)}
                                            </span>
                                            <button
                                                type='button'
                                                onClick={() => setPreviewFile(file)}
                                                className='inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-900 hover:bg-gray-50'
                                            >
                                                <EyeIcon className='h-4 w-4' />
                                                {t('Files.Preview')}
                                            </button>
                                            <button
                                                type='button'
                                                disabled={downloading === file.uuid}
                                                onClick={() => handleDownload(file)}
                                                className='inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-900 hover:bg-gray-50 disabled:opacity-50'
                                            >
                                                <ArrowDownTrayIcon className='h-4 w-4' />
                                                {t('Files.Download')}
                                            </button>
                                            {file.status === 'Processed' &&
                                                <button
                                                    type='button'
                                                    onClick={() => navigate('/analysis/' + file.uuid)}
                                                    className='inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-900 hover:bg-gray-50'
                                                >
                                                    {t('Files.OpenAnalysis')}
                                                </button>
                                            }
                                            <button
                                                type='button'
                                                onClick={() => navigate('/project/' + file.project?.uuid + '?tab=0')}
                                                className='inline-flex items-center gap-2 rounded-lg bg-black px-3 py-1.5 text-sm font-semibold text-white hover:bg-gray-800'
                                            >
                                                <ArrowTopRightOnSquareIcon className='h-4 w-4' />
                                                {t('Files.OpenInProject')}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {previewFile &&
                <FilePreviewModal file={previewFile} handleClose={() => setPreviewFile(null)} />
            }
        </div>
    )
}

export default Files
